import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useKlasisTracker from "../utils/klasisTracker";

import PageContent from "../parts/page-content/PageContent";
import { useParams } from "react-router-dom";
import { RestService } from "../utils/axios";
import { StaticPageResponse } from "../models/StaticPageResponse";
import { ApiRoutes } from "../configuration/apiRoutes";
import axios, { AxiosError } from "axios";
import { Stack } from "@mui/material";
import { Translations } from "../localization/translations";
import parse from "html-react-parser";

import FileList from "../components/FileList";
import { StaticPageFileDto } from "../models/StaticPageFileDto";
import NotFoundBlock from "../components/NotFoundBlock";
import fileDownload from "js-file-download";
import { useAppDispatch } from "../hooks/redux";
import { setStaticPageTitle } from "../features/breadcrumbs";

const StaticPage: React.FC = () => {
    const { path, locale } = useParams();

    const { t } = useTranslation();
    const { klasisTrackPageView } = useKlasisTracker();

    const [response, setResponse] = useState<StaticPageResponse>();
    const [isNotFound, setIsNotFound] = useState(false);

    const handleFileDownload = useCallback((file: StaticPageFileDto) => {
        const url = ApiRoutes.StaticPages.GetFile(file.id);

        axios.get(url, { responseType: "blob" }).then((response) => {
            if (!response) return null;

            fileDownload(
                response.data,
                `${file.fileName}${file.fileExtension}`
            );
        });
    }, []);

    const appDispatch = useAppDispatch();

    useEffect(() => {
        klasisTrackPageView();
    }, [klasisTrackPageView]);

    useEffect(() => {
        if (!path || !locale) {
            return;
        }

        setIsNotFound(false);
        setResponse(undefined);

        RestService.Get<StaticPageResponse>(
            ApiRoutes.StaticPages.Get({
                path: path,
                localeCode: locale,
            }),
            (response: StaticPageResponse) => {
                setResponse(response);
                appDispatch(setStaticPageTitle(response.title));
            },
            (error: AxiosError) => {
                if (error.response?.status === 404) setIsNotFound(true);
            }
        );
    }, [path, locale]);

    if (isNotFound) return <NotFoundBlock />;

    return (
        <PageContent
            title={response?.title}
            showBreadcrumbs={true}
            showTitleBarCommands={true}
        >
            <Stack spacing={2}>
                {response ? (
                    <div
                        style={{
                            overflowWrap: "break-word",
                        }}
                    >
                        {parse(response.text ?? "")}
                    </div>
                ) : null}

                {response?.files && response.files.length > 0 && (
                    <FileList
                        title={t(
                            Translations.staticPages.label.fileAttachments
                        )}
                        files={response.files}
                        onDownaloadClick={handleFileDownload}
                        showFileDate={false}
                    />
                )}
            </Stack>
        </PageContent>
    );
};

export default StaticPage;
